import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  ToggleOffOutlined as ToggleOffOutlinedIcon,
  ToggleOnOutlined as ToggleOnOutlinedIcon,
} from '@mui/icons-material';
import { MenuItem } from '@mui/material';
import apiEndpoints from 'constants/endpoints';
import moment from 'moment';
import { DataGridActionMenu, DataGridListPage } from 'pages/Common';
import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from 'store';
import { addDataGridRow, deleteDataGridRow, editDataGridRow } from 'store/actions';
import { pageKeys } from 'store/reducers/dataGrid.reducer';
import { CouponDialog } from './AddEdit';

type CouponData = {
  id?: number;
  name?: string;
  code?: string;
  discountType?: string;
  discountValue?: number;
  status?: number;
  validFrom?: string | Date | null;
  validUntil?: string | Date | null;
  maxRedemptions?: number;
  redeemedCount?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
};


export const CouponList = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<CouponData | null>(null);
  const dispatch = useAppDispatch();

  const onDelete = useCallback((id: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this coupon permanently?');
    if (confirmed) {
      dispatch(deleteDataGridRow(apiEndpoints.coupons, id, {}, {}, pageKeys.couponList));
    }
  }, [dispatch]);

  const onEdit = useCallback((coupon: CouponData) => {
    setEditingCoupon(coupon);
    setOpenDialog(true);
  }, []);

  const onAdd = useCallback(() => {
    setEditingCoupon(null);
    setOpenDialog(true);
  }, []);

  const onSave = useCallback((data: CouponData) => {
    if (editingCoupon?.id) {
      dispatch(editDataGridRow(apiEndpoints.coupons, editingCoupon.id, data, {}, pageKeys.couponList));
    } else {
      dispatch(addDataGridRow(apiEndpoints.coupons, data, {}, pageKeys.couponList));
    }
  }, [dispatch, editingCoupon]);
  
  const columns = useMemo(() => {

    return [
      {
        field: 'name',
        headerName: 'Name',
        width: 120,
      },
      {
        field: 'code',
        headerName: 'Code',
        width: 120,
      },
      {
        field: 'discountValue',
        headerName: 'Discount Value',
        width: 140,
        valueFormatter: (params: { value: number, data: CouponData }) => {
          if (params.data.discountType === 'percent') {
            return `${params.value}%`;
          }
          return `€${params.value.toFixed(2)}`;
        }
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 100,
        headerClass: 'text-center',
        cellStyle: { textAlign: 'center', paddingTop: '5px' },
        cellRenderer: (params: { value: boolean }) => params.value ? <ToggleOnOutlinedIcon color="success" fontSize="medium" /> : <ToggleOffOutlinedIcon color="disabled" fontSize="medium" />,
      },
      {
        field: 'validFrom',
        headerName: 'Valid From',
        width: 120,
        valueFormatter: (params: { value: string }) => params.value ? moment(params.value).format('Y-MM-DD') : '',  
      },
      {
        field: 'validUntil',
        headerName: 'Valid Until',
        width: 120,
        valueFormatter: (params: { value: string }) => params.value ? moment(params.value).format('Y-MM-DD') : '',
      },
      {
        field: 'maxRedemptions',
        headerName: 'Max Redemptions',
        width: 160,
      },
      {
        field: 'redeemedCount',
        headerName: 'Redeemed',
        width: 120,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        cellStyle: { textAlign: 'center' },
        headerClass: 'text-center',
        flex: 1,
        cellRenderer: ({ data }: { data: CouponData }) => (
          <DataGridActionMenu>
            <MenuItem onClick={() => onEdit(data)}><EditIcon />Edit</MenuItem>
            <MenuItem onClick={() => onDelete(Number(data.id))}><DeleteForeverIcon />Remove</MenuItem>
          </DataGridActionMenu>
        ),
      },

  ]}, [onDelete, onEdit]);

  return <>
    <DataGridListPage
      columns={columns as any}
      filters={[] as any}
      apiEndpoint={apiEndpoints.coupons}
      heading="Coupons"
      rowsPerPage={50}
      pageKey={pageKeys.couponList}
      onAdd={onAdd}
      gridOptions={undefined}
      customHeadingToolbar={undefined}
    />
    <CouponDialog
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      onSave={onSave}
      coupon={editingCoupon}
    />
  </>
}

